// 第三方库
import { makeAutoObservable, runInAction } from 'mobx';

// 内部模块 - 常量
import { STORAGE_KEYS, EXPIRE_TIMES } from '@/constants/constants';

// 内部模块 - 工具
import { logger } from '@/utils/logger';

// 内部模块 - API
import { getCurrentUserService, loginErpBackendService } from '@/apis/authApi';

// 内部模块 - Store
import { chatStore } from './ChatStore';
import { messageStore } from './MessageStore';
import erpDatabaseStore from './ErpDatabaseStore';

// 内部模块 - 工具
import { TokenManager } from '@/utils/TokenManager';

class AccountStore {
  loading = false;
  error = null;
  userInfo = null;
  credentials = null;
  erpUserInfo = null;

  constructor() {
    makeAutoObservable(this);
    // 初始化 Token 管理器
    this.tokenManager = new TokenManager(this);
    // 初始化时加载凭证和用户信息
    this.loadCredentials();
    this.loadErpUserInfo();
    // 尝试从缓存获取用户信息
    const cachedUserInfo = this.getUserInfoFromCache();
    if (cachedUserInfo) {
      runInAction(() => {
        this.userInfo = cachedUserInfo;
      });
    }
  }

  loadCredentials() {
    const credentialsStr = localStorage.getItem(STORAGE_KEYS.CREDENTIALS);
    if (credentialsStr) {
      try {
        runInAction(() => {
          this.credentials = JSON.parse(credentialsStr);
        });
      } catch (error) {
        logger.error('解析凭证信息失败:', error);
        localStorage.removeItem(STORAGE_KEYS.CREDENTIALS);
        runInAction(() => {
          this.credentials = null;
        });
      }
    }
  }

  loadErpUserInfo() {
    const erpUserInfoStr = localStorage.getItem(STORAGE_KEYS.ERP_USER_INFO);
    if (erpUserInfoStr) {
      try {
        runInAction(() => {
          this.erpUserInfo = JSON.parse(erpUserInfoStr);
        });
      } catch (error) {
        logger.error('解析ERP用户信息失败:', error);
        localStorage.removeItem(STORAGE_KEYS.ERP_USER_INFO);
        runInAction(() => {
          this.erpUserInfo = null;
        });
      }
    }
  }

  setLoading(status) {
    runInAction(() => {
      this.loading = status;
    });
  }

  setError(error) {
    runInAction(() => {
      this.error = error;
    });
  }

  setErpUserInfo(token, username, tenantId) {
    const erpUserInfo = {
      token,
      username,
      tenantId,
    };

    // 更新 ERP 用户信息
    runInAction(() => {
      this.erpUserInfo = erpUserInfo;
    });

    // 保存 ERP 用户信息
    localStorage.setItem(STORAGE_KEYS.ERP_USER_INFO, JSON.stringify(erpUserInfo));
  }

  setCredentials(sdkAppId, userSig, chatUserId) {
    // 设置 userSig 过期时间为8小时
    const userSigExpireTime = Date.now() + EXPIRE_TIMES.USER_SIG;

    const credentials = {
      sdkAppId,
      userSig,
      chatUserId,
      userSigExpireTime,
    };

    // 更新聊天系统凭证信息
    runInAction(() => {
      this.credentials = credentials;
    });

    // 保存聊天系统凭证信息
    localStorage.setItem(STORAGE_KEYS.CREDENTIALS, JSON.stringify(credentials));

    // 在新的架构中，凭证由子窗体管理，不再在父窗体中存储
  }

  // 从缓存获取用户信息
  getUserInfoFromCache() {
    const cache = localStorage.getItem(STORAGE_KEYS.USER_INFO_CACHE);
    if (!cache) return null;

    try {
      const { userInfo, timestamp } = JSON.parse(cache);
      // 检查缓存是否过期
      if (Date.now() - timestamp > EXPIRE_TIMES.USER_CACHE) {
        localStorage.removeItem(STORAGE_KEYS.USER_INFO_CACHE);
        return null;
      }
      return userInfo;
    } catch (error) {
      logger.error('解析用户信息缓存失败:', error);
      localStorage.removeItem(STORAGE_KEYS.USER_INFO_CACHE);
      return null;
    }
  }

  // 保存用户信息到缓存
  saveUserInfoToCache(userInfo) {
    const cache = {
      userInfo,
      timestamp: Date.now(),
    };
    localStorage.setItem(STORAGE_KEYS.USER_INFO_CACHE, JSON.stringify(cache));
  }

  async getCurrentUser() {
    // 先检查缓存
    const cachedUserInfo = this.getUserInfoFromCache();
    if (cachedUserInfo) {
      runInAction(() => {
        this.userInfo = cachedUserInfo;
      });
      return this.userInfo;
    }

    // 缓存不存在或已过期，从服务器获取
    const userData = await getCurrentUserService();
    // 根据第三方接口响应格式：{ success: true, code: 0, data: { token: "...", user: {...} } }
    const userInfo = userData.data?.user;
    runInAction(() => {
      this.userInfo = userInfo;
    });
    // 保存到缓存
    this.saveUserInfoToCache(userInfo);
    return this.userInfo;
  }

  // 登录 ERP 后端系统
  async loginErpBackend(tenantName, username, password) {
    const { token, user } = await loginErpBackendService(tenantName, username, password);

    // 保存用户详细信息到本地缓存
    runInAction(() => {
      this.userInfo = user;
    });
    this.saveUserInfoToCache(user);

    // 设置 ERP 用户信息（包含租户ID）
    this.setErpUserInfo(token, username, user.tenantId);

    return { token, user, tenantId: user.tenantId };
  }

  async login(tenantName, username, password) {
    this.setLoading(true);
    this.setError(null);

    // 第一步：登录 ERP 后端
    await this.loginErpBackend(tenantName, username, password);
    // ERP 用户信息已经在 loginErpBackend 中设置，这里不需要重复设置

    // 第二步：不再需要单独的聊天系统登录步骤
    logger.debug('[登录成功] ERP登录完成');

    // 登录成功后检查重定向URL
    const redirectUrl = localStorage.getItem('redirectUrl');
    logger.debug('[登录成功] 检查重定向URL:', redirectUrl);
    if (redirectUrl) {
      // 清除重定向URL
      localStorage.removeItem('redirectUrl');
      logger.debug('[登录成功] 跳转到重定向URL:', redirectUrl);
      window.location.href = redirectUrl; // 跳转回之前的 URL
    } else if (this.fromUrl) {
      logger.debug('[登录成功] 跳转到fromUrl:', this.fromUrl);
      window.location.href = this.fromUrl; // 跳转回之前的 URL
    } else {
      // 默认跳转到首页
      logger.debug('[登录成功] 跳转到默认首页');
      window.location.href = '/';
    }

    this.setLoading(false);
    return true;
  }

  async ensureValidToken() {
    // 如果 token 存在，直接返回
    if (this.erpUserInfo?.token) {
      return this.erpUserInfo.token;
    }
    throw new Error('Token不存在，请重新登录');
  }

  // 委托给 TokenManager
  updateErpToken(newToken) {
    this.tokenManager.updateErpToken(newToken);
  }

  refreshToken() {
    return this.tokenManager.refreshToken();
  }

  async logout(errorMsg) {
    // 记录当前的 URL
    this.fromUrl = window.location.href;
    if (errorMsg) {
      messageStore.error(errorMsg);
    }

    // 保存重定向URL（如果还没有保存的话）
    const currentUrl = window.location.pathname + window.location.search;
    if (!localStorage.getItem('redirectUrl')) {
      localStorage.setItem('redirectUrl', currentUrl);
      logger.debug('[logout] 保存重定向URL:', currentUrl);
    }

    runInAction(() => {
      this.credentials = null;
      this.erpUserInfo = null;
      this.loading = false;
      this.error = null;
      this.userInfo = null;
    });

    // 清空所有 localStorage
    localStorage.clear();
    logger.debug('[logout] 已清空所有 localStorage');

    // 重置聊天状态
    chatStore.reset();
    chatStore.logout();

    // 清理 ERP 数据库缓存
    erpDatabaseStore.reset();

    // 跳转到登录页面
    window.location.href = '/user/login';
  }
}

export const accountStore = new AccountStore();
